import { useState, useCallback, useMemo, useEffect, useRef } from 'preact/hooks';
import { createApiClient, ApiError } from '@/services/api';
import type { Message, WidgetConfig, WidgetSyncState, SerializedMessage } from '@/types';
import { serializeMessages, deserializeMessages } from '@/types';
import { generateUUID } from '@/utils/uuid';

interface UseChatParams {
  config: WidgetConfig;
  sessionId: string;
  userEmail: string | null;
  /** Callback when state changes (for sync service) */
  onStateChange?: (state: WidgetSyncState) => void;
}

/** Preview mode follow-up questions */
const PREVIEW_FOLLOW_UP_QUESTIONS = [
  'What features are included?',
  'How do I get started?',
  'What are the pricing options?',
];

interface UseChatReturn {
  messages: Message[];
  isLoading: boolean;
  isRateLimited: boolean;
  error: string | null;
  /** Whether user has sent at least one message (hides starter suggestions) */
  hasSentMessage: boolean;
  /** Current follow-up questions from last bot response */
  followUpQuestions: string[] | null;
  /** Current input text value */
  inputText: string;
  sendMessage: (content: string) => void;
  submitRating: (messageId: string, rating: 'positive' | 'negative', feedback?: string) => void;
  submitEmail: (questionId: string, email: string) => Promise<void>;
  clearError: () => void;
  addSystemMessage: (content: string, sources?: Array<{ pageUrl: string; title?: string }>) => void;
  /** Set input text value */
  setInputText: (text: string) => void;
  /** Receive synced state from another instance */
  syncState: (state: { messages: SerializedMessage[]; inputText: string; isAwaitingResponse: boolean; followUpQuestions: string[] | null }) => void;
}

export function useChat({ config, sessionId, userEmail, onStateChange }: UseChatParams): UseChatReturn {
  // Get welcome message from settings or use default
  const welcomeMessage = config.settings?.welcomeMessage
    ?? `Hi! I'm here to help you${config.companyName ? ` with ${config.companyName}` : ''}. Ask me anything!`;

  // Get fallback messages from settings
  const postEmailMessage = config.settings?.fallback?.postEmailMessage
    ?? 'Thank you! Our team will reach out to you soon. Is there anything else I can help with?';
  const unansweredMessage = config.settings?.fallback?.unansweredMessage
    ?? "I don't have enough information to answer that.";

  // In preview mode, use mock messages
  const initialMessages = useMemo<Message[]>(() => {
    if (config.isPreviewMode && config.previewMessages) {
      return config.previewMessages.map((msg) => ({
        id: msg.id,
        role: msg.type === 'user' ? 'user' as const : 'bot' as const,
        content: msg.text,
        timestamp: new Date(),
        status: 'sent' as const,
        sources: msg.sources,
        canProvideEmail: msg.canProvideEmail,
        unansweredQuestionId: msg.unansweredQuestionId,
        emailSubmitted: msg.emailSubmitted,
      }));
    }
    return [
      {
        id: 'welcome',
        role: 'bot' as const,
        content: welcomeMessage,
        timestamp: new Date(),
        status: 'sent' as const,
      },
    ];
  }, [config.isPreviewMode, config.previewMessages, welcomeMessage]);

  const [messages, setMessages] = useState<Message[]>(initialMessages);
  const [isLoading, setIsLoading] = useState(false);
  const [isRateLimited, setIsRateLimited] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hasSentMessage, setHasSentMessage] = useState(false);
  const [followUpQuestions, setFollowUpQuestions] = useState<string[] | null>(null);
  const [inputText, setInputTextState] = useState('');

  // Flag to prevent emitting state-update when receiving state-sync
  const isSyncingRef = useRef(false);

  // Sync messages with initialMessages when settings change in preview mode
  useEffect(() => {
    if (config.isPreviewMode) {
      setMessages(initialMessages);
    }
  }, [config.isPreviewMode, initialMessages]);

  // Emit state change when messages or loading state changes
  useEffect(() => {
    if (isSyncingRef.current || !onStateChange) return;

    onStateChange({
      messages: serializeMessages(messages),
      inputText,
      sessionId,
      isAwaitingResponse: isLoading,
      followUpQuestions,
    });
  }, [messages, isLoading, inputText, sessionId, followUpQuestions, onStateChange]);

  const api = useMemo(
    () => createApiClient(config.apiUrl, config.apiKey),
    [config.apiUrl, config.apiKey],
  );

  // Set input text with sync notification
  const setInputText = useCallback((text: string) => {
    setInputTextState(text);
  }, []);

  // Receive synced state from another instance
  const syncState = useCallback((state: { messages: SerializedMessage[]; inputText: string; isAwaitingResponse: boolean; followUpQuestions: string[] | null }) => {
    isSyncingRef.current = true;

    // Update messages
    const deserializedMessages = deserializeMessages(state.messages);
    setMessages(deserializedMessages);

    // Update input text
    setInputTextState(state.inputText);

    // Update loading state
    setIsLoading(state.isAwaitingResponse);

    // Update follow-up questions
    setFollowUpQuestions(state.followUpQuestions);

    // Update hasSentMessage based on messages
    const hasUserMessage = deserializedMessages.some(m => m.role === 'user');
    setHasSentMessage(hasUserMessage);

    // Reset syncing flag after microtask
    queueMicrotask(() => {
      isSyncingRef.current = false;
    });
  }, []);

  const sendMessage = useCallback(
    async (content: string) => {
      if (isRateLimited || isLoading) return;

      const userMsgId = generateUUID();
      const userMessage: Message = {
        id: userMsgId,
        role: 'user',
        content,
        timestamp: new Date(),
        status: 'sending',
      };

      setMessages((prev) => [...prev, userMessage]);
      setIsLoading(true);
      setError(null);
      setHasSentMessage(true);
      setFollowUpQuestions(null);
      setInputTextState(''); // Clear input after sending

      // In preview mode, simulate response without API call
      if (config.isPreviewMode) {
        await new Promise((resolve) => setTimeout(resolve, 500));
        setMessages((prev) =>
          prev.map((m) => (m.id === userMsgId ? { ...m, status: 'sent' as const } : m)),
        );
        setMessages((prev) => [
          ...prev,
          {
            id: generateUUID(),
            role: 'bot' as const,
            content: 'This is a preview response. In production, I would answer your question based on your knowledge base.',
            timestamp: new Date(),
            status: 'sent' as const,
            sources: [
              { pageUrl: 'https://example.com/docs/help', title: 'Help Documentation' },
            ],
            followUpQuestions: PREVIEW_FOLLOW_UP_QUESTIONS,
          },
        ]);
        setFollowUpQuestions(PREVIEW_FOLLOW_UP_QUESTIONS);
        setIsLoading(false);
        return;
      }

      try {
        const response = await api.sendMessage(sessionId, content, userEmail ?? undefined);

        // Mark user message as sent
        setMessages((prev) =>
          prev.map((m) => (m.id === userMsgId ? { ...m, status: 'sent' as const } : m)),
        );

        // Build bot message
        const isUnanswered = response.canProvideEmail && response.unansweredQuestionId;
        const botMessage: Message = {
          id: generateUUID(),
          role: 'bot',
          content: isUnanswered ? unansweredMessage : response.response,
          timestamp: new Date(),
          status: 'sent',
        };

        if (response.messageId) {
          botMessage.messageId = response.messageId;
          botMessage.sources = response.sources;
        }
        if (isUnanswered) {
          botMessage.canProvideEmail = true;
          botMessage.unansweredQuestionId = response.unansweredQuestionId;
        }
        if (response.followUpQuestions && response.followUpQuestions.length > 0) {
          botMessage.followUpQuestions = response.followUpQuestions;
        }

        setMessages((prev) => [...prev, botMessage]);

        // Update follow-up questions state
        setFollowUpQuestions(response.followUpQuestions || null);
      } catch (err) {
        // Mark user message as error
        setMessages((prev) =>
          prev.map((m) => (m.id === userMsgId ? { ...m, status: 'error' as const } : m)),
        );

        if (err instanceof ApiError && err.status === 429) {
          setIsRateLimited(true);
          setError(
            "You've reached the message limit. Would you like to leave your email so we can continue helping?",
          );
        } else {
          setError('Failed to send message. Please try again.');
        }
      } finally {
        setIsLoading(false);
      }
    },
    [api, sessionId, userEmail, isRateLimited, isLoading, config.isPreviewMode, unansweredMessage],
  );

  const submitRating = useCallback(
    async (messageId: string, rating: 'positive' | 'negative', feedback?: string) => {
      try {
        await api.submitRating(messageId, rating, feedback);
        setMessages((prev) =>
          prev.map((m) => (m.messageId === messageId ? { ...m, rating } : m)),
        );
      } catch {
        setError('Failed to submit rating.');
      }
    },
    [api],
  );

  const submitEmail = useCallback(
    async (questionId: string, email: string) => {
      await api.submitEmail(questionId, email);
      setMessages((prev) =>
        prev.map((m) =>
          m.unansweredQuestionId === questionId
            ? { ...m, emailSubmitted: true }
            : m,
        ),
      );
      // Add a thank-you bot message
      setMessages((prev) => [
        ...prev,
        {
          id: generateUUID(),
          role: 'bot' as const,
          content: postEmailMessage,
          timestamp: new Date(),
          status: 'sent' as const,
        },
      ]);
      // Show suggested questions as follow-up options after email submission
      if (config.suggestedQuestions && config.suggestedQuestions.length > 0) {
        setFollowUpQuestions(config.suggestedQuestions);
      }
    },
    [api, config.suggestedQuestions, postEmailMessage],
  );

  const clearError = useCallback(() => setError(null), []);

  const addSystemMessage = useCallback(
    (content: string, sources?: Array<{ pageUrl: string; title?: string }>) => {
      setMessages((prev) => [
        ...prev,
        {
          id: generateUUID(),
          role: 'bot' as const,
          content,
          timestamp: new Date(),
          status: 'sent' as const,
          sources,
        },
      ]);
    },
    [],
  );

  return {
    messages,
    isLoading,
    isRateLimited,
    error,
    hasSentMessage,
    followUpQuestions,
    inputText,
    sendMessage,
    submitRating,
    submitEmail,
    clearError,
    addSystemMessage,
    setInputText,
    syncState,
  };
}
